/**
 * Add-expense draft: the "slip" being filled in, and the rules that decide which step is
 * active, what is done and whether Save is allowed. Pure functions, unit-tested.
 */
import type { Entry } from '../api/types.ts';
import { isValidItemName } from '../lib/items.ts';
import { MAX_EXPENSE } from '../lib/money.ts';
import type { Unit } from '../lib/units.ts';

export type Step = 'what' | 'item' | 'amount' | 'proof';
export type ReasonKey = 'shop' | 'street' | 'lost' | 'other';
export const REASONS: readonly ReasonKey[] = ['shop', 'street', 'lost', 'other'];
export const MAX_PHOTOS = 3;
export const MIN_REASON_LENGTH = 5;

export interface Photo {
  id: string;
  blob: Blob;
  url: string;
}

export interface Draft {
  entityId: string;
  categoryId: string | null;
  itemName: string;
  /** 0 = not entered yet. Quantity and unit are both required (owner's request, 22 Oct 2026). */
  quantity: number;
  unit: Unit | null;
  /** Digits typed on the keypad; '' = nothing yet. */
  amount: string;
  photos: Photo[];
  noReceipt: boolean;
  reason: ReasonKey | null;
  reasonText: string;
  txnDate: string;
  /** Entry being corrected after the owner sent it back. */
  correctsId: string | null;
  /** Amount of the entry being corrected; it is already out of the balance. */
  correctsAmount: number;
  /** Another company / house paid the shop (Dues): his wallet stays the same. */
  paidBy: string | null;
}

export function emptyDraft(entityId: string, today: string): Draft {
  return {
    entityId,
    categoryId: null,
    itemName: '',
    quantity: 0,
    unit: null,
    amount: '',
    photos: [],
    noReceipt: false,
    reason: null,
    reasonText: '',
    txnDate: today,
    correctsId: null,
    correctsAmount: 0,
    paidBy: null,
  };
}

export function amountOf(d: Draft): number {
  return Number(d.amount || 0);
}

/** Cash this entry may use: wallet balance plus the corrected entry's old amount. */
export function available(d: Draft, balance: number): number {
  return balance + d.correctsAmount;
}

export function isOverBalance(d: Draft, balance: number): boolean {
  // Another house paid the shop: nothing comes out of his cash.
  return d.paidBy === null && amountOf(d) > available(d, balance);
}

export function reasonText(d: Draft, labels: Record<Exclude<ReasonKey, 'other'>, string>): string | null {
  if (!d.noReceipt || d.photos.length > 0 || !d.reason) return null;
  return d.reason === 'other' ? d.reasonText.trim() : labels[d.reason];
}

export function isReasonOk(d: Draft): boolean {
  if (!d.reason) return false;
  return d.reason !== 'other' || d.reasonText.trim().length >= MIN_REASON_LENGTH;
}

export interface Progress {
  itemDone: boolean;
  /** A quantity above 0 and its unit. */
  quantityDone: boolean;
  amountDone: boolean;
  proofDone: boolean;
}

export function progress(d: Draft, balance: number): Progress {
  const amt = amountOf(d);
  return {
    itemDone: d.categoryId !== null && isValidItemName(d.itemName),
    quantityDone: d.quantity > 0 && d.unit !== null,
    amountDone: amt >= 1 && amt <= MAX_EXPENSE && !isOverBalance(d, balance),
    proofDone: d.photos.length > 0 || (d.noReceipt && isReasonOk(d)),
  };
}

export type SaveHint = 'hintPhoto' | 'hintReason' | 'hintOther' | null;

/** Why Save is disabled on the proof step (null when it is enabled). */
export function saveHint(d: Draft, balance: number): SaveHint {
  const p = progress(d, balance);
  if (p.proofDone) return null;
  if (!d.noReceipt) return 'hintPhoto';
  if (!d.reason) return 'hintReason';
  return 'hintOther';
}

export function canSave(d: Draft, balance: number): boolean {
  const p = progress(d, balance);
  return p.itemDone && p.quantityDone && p.amountDone && p.proofDone;
}

export type AmountHint = 'needQty' | 'needUnit' | 'needAmount' | null;

/** What is still missing on the "How much?" step (null when Next can be pressed). */
export function amountHint(d: Draft): AmountHint {
  if (d.quantity <= 0) return 'needQty';
  if (d.unit === null) return 'needUnit';
  if (amountOf(d) < 1) return 'needAmount';
  return null;
}

/** The step a tap on the slip's item row should open. */
export function itemRowTarget(d: Draft): Step {
  return d.categoryId && !d.itemName ? 'item' : 'what';
}

/** Step number shown in the deck header (1 / 3, 2 / 3, 3 / 3). */
export function stepNumber(step: Step): 1 | 2 | 3 {
  return step === 'amount' ? 2 : step === 'proof' ? 3 : 1;
}

export interface RecentSave {
  entityId: string;
  categoryId: string;
  amount: number;
  savedAt: number;
}

/**
 * What he already saved on the picked date for this wallet (not rejected, not the entry being
 * corrected), shown under the date so an older day isn't entered twice.
 */
export function sameDayOf(
  entries: readonly Pick<Entry, 'id' | 'entityId' | 'txnDate' | 'status' | 'amount'>[],
  d: Pick<Draft, 'entityId' | 'txnDate' | 'correctsId'>,
): { count: number; total: number } | null {
  const rows = entries.filter(
    (e) => e.entityId === d.entityId && e.txnDate === d.txnDate && e.status !== 'rejected' && e.id !== d.correctsId,
  );
  return rows.length ? { count: rows.length, total: rows.reduce((a, e) => a + e.amount, 0) } : null;
}

/** SPEC §7: same spender, entity, amount and category within 10 minutes. */
export const DUPLICATE_WINDOW_MS = 10 * 60 * 1000;

export function isLikelyDuplicate(d: Draft, recent: readonly RecentSave[], now: number): boolean {
  if (d.correctsId) return false;
  return recent.some(
    (r) => r.entityId === d.entityId && r.categoryId === d.categoryId && r.amount === amountOf(d) && now - r.savedAt < DUPLICATE_WINDOW_MS,
  );
}
